export const DEFAULT_CATALOG_FILTERS = {
  sortBy: '',
  item: [],
  collection: [],
  style: [],
  metal: [],
  stone: [],
  size: [],
  status: [],
  price: null,
};

/** Fallback when no sort is selected in the filter panel. */
export const DEFAULT_ITEM_DATA_SORT = 'price_asc';

export const createDefaultCatalogFilters = () => ({
  sortBy: DEFAULT_CATALOG_FILTERS.sortBy,
  item: [],
  collection: [],
  style: [],
  metal: [],
  stone: [],
  size: [],
  status: [],
  price: null,
});

/** Pre-select navigated subcategory in the filter panel (Item / Collection / Style tabs). */
export const createCatalogFiltersForSubCategory = (
  categoryId,
  subCategoryId
) => {
  const filters = createDefaultCatalogFilters();
  if (!subCategoryId) return filters;
  if (categoryId === 'Item') {
    filters.item = [subCategoryId];
  } else if (categoryId === 'Collection') {
    filters.collection = [subCategoryId];
  } else if (categoryId === 'Style') {
    filters.style = [subCategoryId];
  }
  return filters;
};

/** Maps filter panel sort value to `home/itemData` sale param. */
export const sortByToSale = (sortBy) => {
  switch (sortBy) {
    case 'price_desc':
      return 'top_sale';
    case 'price_asc':
      return 'lowest_sale';
    case 'name_asc':
      return 'name_asc';
    case 'name_desc':
      return 'name_desc';
    default:
      return 'lowest_sale';
  }
};

export const catalogFilterType = (optionDetails) =>
  optionDetails ? 'catalog' : 'inventory';

const sortFilterOptionsByName = (list) =>
  [...(list || [])].sort((a, b) =>
    (a?.name || '').localeCompare(b?.name || '', undefined, {
      sensitivity: 'base',
    })
  );

/** First option in name-sorted filter list (toolbar dropdown order). */
export const getFirstFilterOptionId = (options) => {
  const sorted = sortFilterOptionsByName(options);
  return sorted[0]?._id ?? null;
};

/**
 * Inventory mode: if Item/Collection selection is not in loaded options,
 * fall back to the first option (0th index after name sort).
 */
export const reconcileFiltersForInventoryMode = (filters, filterData, categoryId) => {
  if (!filterData || !filters) return filters;

  let sectionKey = null;
  let dataKey = null;
  if (categoryId === 'Item') {
    sectionKey = 'item';
    dataKey = 'item';
  } else if (categoryId === 'Collection') {
    sectionKey = 'collection';
    dataKey = 'collection';
  } else {
    return filters;
  }

  const options = filterData[dataKey] || [];
  const validIds = new Set(
    options.map((entry) => entry?._id).filter(Boolean)
  );
  const selected = filters[sectionKey] || [];
  if (!selected.length) return filters;

  const allValid = selected.every((id) => validIds.has(id));
  if (allValid) return filters;

  const next = { ...filters };
  const firstId = getFirstFilterOptionId(options);
  next[sectionKey] = firstId ? [firstId] : [];
  return next;
};

/** Keys excluded from the filter badge (Style toolbar dropdown only). */
export const getFilterCountExcludeKeys = (categoryId) => {
  if (categoryId === 'Style') return ['style'];
  return [];
};

export const countCatalogFilterSelections = (
  filters,
  { priceBounds, excludeKeys = [] } = {}
) => {
  let count = 0;
  if (filters.sortBy) {
    count += 1;
  }
  ['item', 'collection', 'style', 'metal', 'stone', 'size', 'status'].forEach((key) => {
    if (excludeKeys.includes(key)) return;
    count += filters[key]?.length || 0;
  });
  if (
    filters.price?.length === 2 &&
    priceBounds &&
    (filters.price[0] !== 0 || filters.price[1] !== priceBounds.max)
  ) {
    count += 1;
  }
  return count;
};

export const countSectionSelections = (filters, key) => {
  if (key === 'sort') {
    return filters.sortBy ? 1 : 0;
  }
  return filters[key]?.length || 0;
};
